import * as React from 'react'
import { useTheme } from '@mui/material/styles'
import Box from '@mui/material/Box'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableContainer from '@mui/material/TableContainer'
import TableRow from '@mui/material/TableRow'
import { Stack, Typography } from '@mui/material'
import { stableSort, getComparator, Order, HeadCell } from '../../utils/tableFunctions'
import TextSearchField from '../common/TextSearchField'
import DateRangePicker from '../common/DateRangePicker'
import { RouteConstants } from '../../navigation/navigation-types'
import { EventOfContractorDto } from '../../services/api/sharedTypes/Event'
import EnhancedTableHead from '../table/EnhancedTableHead'
import CustomTableCell from '../common/CustomTableCell'
import PaymentStateTag from '../common/PaymentStateTag'
import { convertToSearchString, getNumberRoundedInFormattedString } from '../../utils/global-utils'
import { getEventTableRowColor } from '../../utils/event-utils'
import CancelFiltersButton from '../common/CancelFiltersButton'
import TableButtonOpen from '../table/TableButtonOpen'

const headCells: readonly HeadCell[] = [
    {
        id: 'eventName',
        numeric: false,
        disablePadding: false,
        label: 'Název',
    },
    {
        id: 'startsAt',
        numeric: false,
        disablePadding: false,
        label: 'Datum od',
    },
    {
        id: 'endsAt',
        numeric: false,
        disablePadding: false,
        label: 'Datum do',
    },
    {
        id: 'costs',
        numeric: true,
        disablePadding: false,
        label: 'Náklady',
    },
    {
        id: 'paymentState',
        numeric: false,
        disablePadding: false,
        label: 'Zaplaceno',
        centered: true,
    },
    {
        id: 'buttons',
        numeric: false,
        disablePadding: false,
        label: '',
    },
]
interface EventTableProps {
    events: EventOfContractorDto[]
    totalCosts: number
}

const ContractorEventTable: React.FC<EventTableProps> = ({ events, totalCosts }: EventTableProps) => {
    const [order, setOrder] = React.useState<Order>('desc')
    const [orderBy, setOrderBy] = React.useState<string>('startsAt')
    const [dateFrom, setDateFrom] = React.useState<Date | null>(null)
    const [dateTo, setDateTo] = React.useState<Date | null>(null)
    const [filteredEvents, setFilteredEvents] = React.useState<EventOfContractorDto[]>(events)
    const [searchedValue, setSearchedValue] = React.useState<string>('')

    const theme = useTheme()

    // Filter events
    React.useEffect(() => {
        const filtered = events.filter((row) => {
            const date = new Date(row.startsAt)
            if (dateFrom && date < dateFrom) {
                return false
            }
            if (dateTo && date > dateTo) {
                return false
            }
            if (searchedValue && !convertToSearchString(row.eventName).includes(convertToSearchString(searchedValue))) {
                return false
            }
            return true
        })
        setFilteredEvents(filtered)
    }, [events, dateFrom, dateTo, searchedValue])

    const handleRequestSort = (event: React.MouseEvent<unknown>, property: string) => {
        const isAsc = orderBy === property && order === 'asc'
        setOrder(isAsc ? 'desc' : 'asc')
        setOrderBy(property)
    }

    return (
        <>
            <Typography variant='h6' color='primary' mt={3}>
                Události
            </Typography>

            <Stack pt={2} pb={3} direction='row' spacing={3}>
                <DateRangePicker
                    labelFrom='Datum od'
                    labelTo='Datum do'
                    onSetDateFrom={setDateFrom}
                    onSetDateTo={setDateTo}
                    dateFrom={dateFrom}
                    dateTo={dateTo}
                />
                <TextSearchField value={searchedValue} onChange={(e) => setSearchedValue(e.target.value)} />
                {
                    searchedValue || dateFrom || dateTo ? (
                        <CancelFiltersButton onClick={
                            () => {
                                setSearchedValue('')
                                setDateFrom(null)
                                setDateTo(null)
                            }
                        }
                        />
                    ) : null
                }
            </Stack>
            <Typography variant='body2' style={{ marginBottom: theme.spacing(3) }}>
                {filteredEvents.length} záznamů
            </Typography>
            <Box style={{ width: '100%' }}>
                <TableContainer>
                    <Table>
                        <EnhancedTableHead
                            order={order}
                            orderBy={orderBy}
                            onRequestSort={handleRequestSort}
                            rowCount={filteredEvents.length}
                            headCells={headCells}
                        />
                        <TableBody>
                            {stableSort(
                                filteredEvents.map((row) => {
                                    return {
                                        ...row,
                                        startsAt: new Date(row.startsAt).getTime(),
                                        endsAt: new Date(row.endsAt).getTime(),
                                        isLeader: row.isLeader ? 'Ano' : 'Ne',
                                        gotPaid: row.gotPaid ? 'Ano' : 'Ne',
                                        isConfirmed: row.isConfirmed ? 'Ano' : 'Ne',
                                        hasResponded: row.hasResponded ? 'Ano' : 'Ne',
                                    }
                                }),
                                getComparator(order, orderBy)
                            ).map((row, index: number) => {
                                return (
                                    <TableRow
                                        hover
                                        tabIndex={-1}
                                        key={index}
                                        style={{
                                            height: '42px',
                                            cursor: 'pointer',
                                            backgroundColor: getEventTableRowColor(
                                                row.paymentState,
                                                theme,
                                            ),
                                        }}
                                    >
                                        <CustomTableCell route={RouteConstants.events + '/' + row.eventId} isText>
                                            {row.eventName}
                                        </CustomTableCell>
                                        <CustomTableCell route={RouteConstants.events + '/' + row.eventId} isText>
                                            {new Date(row.startsAt).toLocaleDateString('cs')}
                                        </CustomTableCell>
                                        <CustomTableCell route={RouteConstants.events + '/' + row.eventId} isText>
                                            {new Date(row.endsAt).toLocaleDateString('cs')}
                                        </CustomTableCell>
                                        <CustomTableCell route={RouteConstants.events + '/' + row.eventId} isText>
                                            {getNumberRoundedInFormattedString(row.costs)}
                                        </CustomTableCell>
                                        <CustomTableCell route={RouteConstants.events + '/' + row.eventId} center>
                                            <PaymentStateTag paymentState={row.paymentState} />
                                        </CustomTableCell>
                                        <CustomTableCell route={RouteConstants.events + '/' + row.eventId} align='right'>
                                            <TableButtonOpen route={RouteConstants.events + '/' + row.eventId} />
                                        </CustomTableCell>
                                    </TableRow>
                                )
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
                <Stack spacing={2} direction='row' py={3} ml={3} mb={5}>
                    <Typography variant='body2' fontWeight='bold'>
                        Celkem náklady:
                    </Typography>
                    <Typography variant='body2'>{getNumberRoundedInFormattedString(totalCosts)}</Typography>
                </Stack>
            </Box>
        </>
    )
}

export default ContractorEventTable
